import { useState, useEffect } from 'react';
import { Expense } from '../../../services/supabase';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CurrencySelect } from '../../../components/CurrencySelect';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MoreVertical, Edit, Trash2, ChevronDown, ChevronUp, Check, X, ArrowRightLeft } from 'lucide-react';
import { useExpenses } from '../../../context/ExpensesContext';
import { useAuth } from '../../../context/AuthContext';
import { useConversion } from '../../../hooks/useConversion';

interface ExpenseRowProps {
  expense: Expense;
}

export const ExpenseRow = ({ expense }: ExpenseRowProps) => {
  const { updateExpense, deleteExpense } = useExpenses();
  const { supabaseUser } = useAuth();
  const { convertExpenseAmount } = useConversion();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isConvertDialogOpen, setIsConvertDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [targetCurrency, setTargetCurrency] = useState(expense.converted_currency || supabaseUser?.preferred_currency || 'USD');
  const [convertedAmount, setConvertedAmount] = useState<number | null>(expense.converted_amount || null);
  const [exchangeRate, setExchangeRate] = useState<number | null>(expense.exchange_rate || null);
  const [isLoadingRate, setIsLoadingRate] = useState(false);

  // Check if current conversion differs from user's preferred currency
  const needsConversionUpdate = expense.converted_currency !== (supabaseUser?.preferred_currency || 'USD');

  // Edit form state
  const [editForm, setEditForm] = useState({
    description: expense.description,
    category: expense.category,
    amount: expense.amount.toString(),
    base_amount: expense.base_amount?.toString() || '',
    tax: expense.tax?.toString() || '',
    tip: expense.tip?.toString() || '',
    fees: expense.fees?.toString() || '',
    currency: expense.currency || 'USD',
    expense_date: expense.expense_date,
    notes: expense.notes || '',
    is_planned: expense.is_planned || false,
    original_file_name: (expense as Record<string, unknown>).original_file_name as string || '',
  });

  // Fetch conversion rate when currency changes
  useEffect(() => {
    const fetchConversionRate = async () => {
      if (!isConvertDialogOpen) return;

      // If currency hasn't changed from current, use existing values
      if (targetCurrency === expense.converted_currency) {
        setConvertedAmount(expense.converted_amount || null);
        setExchangeRate(expense.exchange_rate || null);
        return;
      }

      // Reset if same as original currency
      if (targetCurrency === expense.currency) {
        setConvertedAmount(expense.amount);
        setExchangeRate(1.0);
        return;
      }

      setIsLoadingRate(true);
      try {
        const result = await convertExpenseAmount(
          expense.amount,
          expense.currency || 'USD',
          targetCurrency,
          expense.expense_date
        );

        setConvertedAmount(result.converted_amount);
        setExchangeRate(result.exchange_rate);
      } catch (error) {
        console.error('Error fetching conversion rate:', error);
        setConvertedAmount(null);
        setExchangeRate(null);
      } finally {
        setIsLoadingRate(false);
      }
    };

    fetchConversionRate();
  }, [targetCurrency, isConvertDialogOpen, expense.amount, expense.currency, expense.expense_date, expense.converted_amount, expense.converted_currency, expense.exchange_rate, convertExpenseAmount]);

  const handleEdit = () => {
    setIsEditing(true);
    setIsExpanded(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    // Reset form to original values
    setEditForm({
      description: expense.description,
      category: expense.category,
      amount: expense.amount.toString(),
      base_amount: expense.base_amount?.toString() || '',
      tax: expense.tax?.toString() || '',
      tip: expense.tip?.toString() || '',
      fees: expense.fees?.toString() || '',
      currency: expense.currency || 'USD',
      expense_date: expense.expense_date,
      notes: expense.notes || '',
      is_planned: expense.is_planned || false,
      original_file_name: (expense as Record<string, unknown>).original_file_name as string || '',
    });
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      // Calculate total if base_amount is provided
      let finalAmount = parseFloat(editForm.amount);
      if (editForm.base_amount) {
        finalAmount =
          parseFloat(editForm.base_amount || '0') +
          parseFloat(editForm.tax || '0') +
          parseFloat(editForm.tip || '0') +
          parseFloat(editForm.fees || '0');
      }

      await updateExpense(expense.id, {
        description: editForm.description,
        category: editForm.category,
        amount: finalAmount,
        base_amount: editForm.base_amount ? parseFloat(editForm.base_amount) : null,
        tax: editForm.tax ? parseFloat(editForm.tax) : null,
        tip: editForm.tip ? parseFloat(editForm.tip) : null,
        fees: editForm.fees ? parseFloat(editForm.fees) : null,
        currency: editForm.currency,
        expense_date: editForm.expense_date,
        notes: editForm.notes || undefined,
        is_planned: editForm.is_planned,
        original_file_name: editForm.original_file_name || undefined,
      });

      setIsEditing(false);
    } catch (error) {
      console.error('Error updating expense:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteExpense(expense.id);
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting expense:', error);
    }
  };

  const handleConvert = async () => {
    try {
      setIsConverting(true);

      // Convert the original expense amount to the target currency
      const conversionResult = await convertExpenseAmount(
        expense.amount,
        expense.currency || 'USD',
        targetCurrency,
        expense.expense_date
      );

      // Update ONLY the converted fields, keep original amount/currency intact
      await updateExpense(expense.id, {
        converted_amount: conversionResult.converted_amount,
        converted_currency: conversionResult.converted_currency,
        exchange_rate: conversionResult.exchange_rate,
        rate_date: conversionResult.rate_date,
      });

      // Reset state and close dialog
      setIsConvertDialogOpen(false);
      setConvertedAmount(expense.converted_amount || null);
      setExchangeRate(expense.exchange_rate || null);
      setTargetCurrency(expense.converted_currency || supabaseUser?.preferred_currency || 'USD');
    } catch (error) {
      console.error('Error converting expense:', error);
      alert('Failed to update conversion. Please try again.');
    } finally {
      setIsConverting(false);
    }
  };

  const getCategoryBadgeColor = (category: string) => {
    const hash = category.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const colors = ['default', 'secondary', 'outline'];
    return colors[hash % colors.length];
  };

  const hasBreakdown = expense.base_amount || expense.tax || expense.tip || expense.fees;

  return (
    <>
      <div
        className="group relative border-b last:border-b-0 hover:bg-muted/30 transition-colors"
      >
        <div
          className="flex items-center gap-4 p-4 cursor-pointer"
          onClick={() => !isEditing && setIsExpanded(!isExpanded)}
        >
          {/* Date */}
          <div className="w-24 flex-shrink-0 text-sm text-muted-foreground">
            {new Date(expense.expense_date).toLocaleDateString()}
          </div>

          {/* Category */}
          <div className="w-32 flex-shrink-0">
            <Badge variant={getCategoryBadgeColor(expense.category) as any}>
              {expense.category}
            </Badge>
          </div>

          {/* Description */}
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{expense.description}</div>
            {hasBreakdown && !isExpanded && (
              <div className="text-xs text-muted-foreground">
                {expense.base_amount && `Base: ${expense.currency} ${expense.base_amount}`}
                {expense.tax && ` + Tax: ${expense.currency} ${expense.tax}`}
                {expense.tip && ` + Tip: ${expense.currency} ${expense.tip}`}
              </div>
            )}
          </div>

          {/* Amount */}
          <div className="w-32 flex-shrink-0 text-right font-semibold">
            {expense.currency} {expense.amount.toFixed(2)}
          </div>

          {/* Actions - visible on hover */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {/* Expand/Collapse button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              className="h-8 w-8 p-0"
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>

            {/* Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEdit(); }}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setIsConvertDialogOpen(true); }}>
                  <ArrowRightLeft className="h-4 w-4 mr-2" />
                  Conversion {needsConversionUpdate && <span className="ml-1 text-orange-500">*</span>}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => { e.stopPropagation(); setIsDeleteDialogOpen(true); }}
                  className="text-red-600"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Expanded details or edit form */}
        {isExpanded && (
          <div className="px-4 pb-4 border-t bg-muted/20" onClick={(e) => e.stopPropagation()}>
            {isEditing ? (
              // Edit Mode
              <div className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* Description */}
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    />
                  </div>

                  {/* Category */}
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select
                      value={editForm.category}
                      onValueChange={(value) => setEditForm({ ...editForm, category: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Accommodation">Accommodation</SelectItem>
                        <SelectItem value="Food & Dining">Food & Dining</SelectItem>
                        <SelectItem value="Transportation">Transportation</SelectItem>
                        <SelectItem value="Activities">Activities</SelectItem>
                        <SelectItem value="Entertainment">Entertainment</SelectItem>
                        <SelectItem value="Shopping">Shopping</SelectItem>
                        <SelectItem value="Health & Wellness">Health & Wellness</SelectItem>
                        <SelectItem value="Emergency">Emergency</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date */}
                  <div>
                    <Label htmlFor="expense_date">Date</Label>
                    <Input
                      id="expense_date"
                      type="date"
                      value={editForm.expense_date}
                      onChange={(e) => setEditForm({ ...editForm, expense_date: e.target.value })}
                    />
                  </div>

                  {/* Currency */}
                  <div>
                    <Label htmlFor="currency">Currency</Label>
                    <CurrencySelect
                      value={editForm.currency}
                      onValueChange={(value) => setEditForm({ ...editForm, currency: value })}
                    />
                  </div>
                </div>

                {/* Amount breakdown */}
                <div className="border rounded-lg p-4 bg-background">
                  <Label className="text-sm font-semibold mb-3 block">Amount Breakdown</Label>
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <Label htmlFor="base_amount" className="text-xs">Base Amount</Label>
                      <Input
                        id="base_amount"
                        type="number"
                        step="0.01"
                        value={editForm.base_amount}
                        onChange={(e) => {
                          const newBase = e.target.value;
                          const update: typeof editForm = { ...editForm, base_amount: newBase };
                          if (newBase) {
                            update.amount = (parseFloat(newBase || '0') + parseFloat(editForm.tax || '0') + parseFloat(editForm.tip || '0') + parseFloat(editForm.fees || '0')).toFixed(2);
                          }
                          setEditForm(update);
                        }}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label htmlFor="tax" className="text-xs">Tax</Label>
                      <Input
                        id="tax"
                        type="number"
                        step="0.01"
                        value={editForm.tax}
                        onChange={(e) => {
                          const newTax = e.target.value;
                          const update: typeof editForm = { ...editForm, tax: newTax };
                          if (editForm.base_amount) {
                            update.amount = (parseFloat(editForm.base_amount || '0') + parseFloat(newTax || '0') + parseFloat(editForm.tip || '0') + parseFloat(editForm.fees || '0')).toFixed(2);
                          }
                          setEditForm(update);
                        }}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label htmlFor="tip" className="text-xs">Tip</Label>
                      <Input
                        id="tip"
                        type="number"
                        step="0.01"
                        value={editForm.tip}
                        onChange={(e) => {
                          const newTip = e.target.value;
                          const update: typeof editForm = { ...editForm, tip: newTip };
                          if (editForm.base_amount) {
                            update.amount = (parseFloat(editForm.base_amount || '0') + parseFloat(editForm.tax || '0') + parseFloat(newTip || '0') + parseFloat(editForm.fees || '0')).toFixed(2);
                          }
                          setEditForm(update);
                        }}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <Label htmlFor="fees" className="text-xs">Fees</Label>
                      <Input
                        id="fees"
                        type="number"
                        step="0.01"
                        value={editForm.fees}
                        onChange={(e) => {
                          const newFees = e.target.value;
                          const update: typeof editForm = { ...editForm, fees: newFees };
                          if (editForm.base_amount) {
                            update.amount = (parseFloat(editForm.base_amount || '0') + parseFloat(editForm.tax || '0') + parseFloat(editForm.tip || '0') + parseFloat(newFees || '0')).toFixed(2);
                          }
                          setEditForm(update);
                        }}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  {editForm.base_amount && (
                    <div className="mt-3 pt-3 border-t text-sm">
                      <div className="flex justify-between font-semibold">
                        <span>Total:</span>
                        <span>
                          {editForm.currency}{' '}
                          {(
                            parseFloat(editForm.base_amount || '0') +
                            parseFloat(editForm.tax || '0') +
                            parseFloat(editForm.tip || '0') +
                            parseFloat(editForm.fees || '0')
                          ).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Input
                    id="notes"
                    value={editForm.notes}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                    placeholder="Additional notes..."
                  />
                </div>

                {/* Original File Name */}
                {expense.receipt_url && (
                  <div>
                    <Label htmlFor="original_file_name">Receipt File Name</Label>
                    <Input
                      id="original_file_name"
                      value={editForm.original_file_name}
                      onChange={(e) => setEditForm({ ...editForm, original_file_name: e.target.value })}
                      placeholder="e.g. receipt_hotel.pdf"
                    />
                  </div>
                )}

                {/* Is Planned */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_planned"
                    checked={editForm.is_planned}
                    onChange={(e) => setEditForm({ ...editForm, is_planned: e.target.checked })}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="is_planned" className="cursor-pointer">
                    This is a planned expense
                  </Label>
                </div>

                {/* Action buttons */}
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    <Check className="h-4 w-4 mr-1" />
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            ) : (
              // View Mode
              <div className="space-y-3 pt-4">
                {/* Amount breakdown */}
                {hasBreakdown && (
                  <div className="bg-background border rounded-lg p-3">
                    <div className="text-xs font-semibold text-muted-foreground mb-2">Amount Breakdown</div>
                    <div className="space-y-1 text-sm">
                      {expense.base_amount && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Base amount:</span>
                          <span className="font-medium">{expense.currency} {expense.base_amount.toFixed(2)}</span>
                        </div>
                      )}
                      {expense.tax && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Tax:</span>
                          <span className="font-medium">{expense.currency} {expense.tax.toFixed(2)}</span>
                        </div>
                      )}
                      {expense.tip && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Tip:</span>
                          <span className="font-medium">{expense.currency} {expense.tip.toFixed(2)}</span>
                        </div>
                      )}
                      {expense.fees && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Fees:</span>
                          <span className="font-medium">{expense.currency} {expense.fees.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between pt-2 border-t font-semibold">
                        <span>Total:</span>
                        <span>{expense.currency} {expense.amount.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes */}
                {expense.notes && (
                  <div>
                    <div className="text-xs font-semibold text-muted-foreground mb-1">Notes</div>
                    <div className="text-sm">{expense.notes}</div>
                  </div>
                )}

                {/* Receipt */}
                {expense.receipt_url && (
                  <div>
                    <div className="text-xs font-semibold text-muted-foreground mb-1">Receipt</div>
                    <a
                      href={expense.receipt_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline"
                    >
                      View receipt{(expense as Record<string, unknown>).original_file_name ? ` (${(expense as Record<string, unknown>).original_file_name})` : ''}
                    </a>
                  </div>
                )}

                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4 text-xs text-muted-foreground pt-2 border-t">
                  <div>
                    <span className="font-semibold">Created: </span>
                    {new Date(expense.created_at!).toLocaleString()}
                  </div>
                  {expense.updated_at && expense.updated_at !== expense.created_at && (
                    <div>
                      <span className="font-semibold">Updated: </span>
                      {new Date(expense.updated_at).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Expense?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this expense? This action cannot be undone.
              <div className="mt-4 p-3 bg-muted rounded-lg">
                <div className="font-medium">{expense.description}</div>
                <div className="text-sm text-muted-foreground">
                  {expense.currency} {expense.amount.toFixed(2)} · {new Date(expense.expense_date).toLocaleDateString()}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Currency Conversion Dialog */}
      <Dialog open={isConvertDialogOpen} onOpenChange={setIsConvertDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Conversion
              {needsConversionUpdate && (
                <span className="ml-2 text-sm font-normal text-orange-500">
                  * Differs from preferred currency
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              View and update how this expense is converted for reporting using the exchange rate from {new Date(expense.expense_date).toLocaleDateString()}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Original expense info (read-only) */}
            <div className="p-3 bg-muted rounded-lg border-l-4 border-blue-500">
              <div className="text-xs font-semibold text-muted-foreground mb-2">Original Expense</div>
              <div className="font-medium">{expense.description}</div>
              <div className="text-sm mt-1">
                <span className="font-semibold">{expense.currency} {expense.amount.toFixed(2)}</span>
                <span className="text-muted-foreground ml-2">• {new Date(expense.expense_date).toLocaleDateString()}</span>
              </div>
            </div>

            {/* Current conversion info */}
            {expense.converted_currency && expense.converted_amount && (
              <div className={`p-3 rounded-lg border-l-4 ${needsConversionUpdate ? 'bg-orange-50 dark:bg-orange-950 border-orange-500' : 'bg-green-50 dark:bg-green-950 border-green-500'}`}>
                <div className="text-xs font-semibold text-muted-foreground mb-2">
                  Current Conversion
                  {needsConversionUpdate && (
                    <span className="ml-2 text-orange-600 dark:text-orange-400">
                      (Your preferred: {supabaseUser?.preferred_currency || 'USD'})
                    </span>
                  )}
                </div>
                <div className="text-sm">
                  <span className="font-semibold">{expense.converted_currency} {expense.converted_amount.toFixed(2)}</span>
                  {expense.exchange_rate && (
                    <span className="text-muted-foreground ml-2 text-xs font-mono">
                      @ {expense.exchange_rate.toFixed(4)}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Target currency selector */}
            <div className="space-y-2">
              <Label htmlFor="target_currency">
                Update Converted Currency:
                {needsConversionUpdate && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    (Change to match your preferred currency)
                  </span>
                )}
              </Label>
              <CurrencySelect
                value={targetCurrency}
                onValueChange={setTargetCurrency}
              />
            </div>

            {/* New converted amount preview (only if changed) */}
            {targetCurrency !== expense.converted_currency && (
              <div className="p-4 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="text-xs font-semibold text-muted-foreground mb-3">New Converted Amount</div>
                {isLoadingRate ? (
                  <div className="text-sm text-muted-foreground">Loading conversion rate...</div>
                ) : convertedAmount !== null && exchangeRate !== null ? (
                  <div className="space-y-2">
                    <div className="flex items-baseline justify-between">
                      <span className="text-sm text-muted-foreground">Amount:</span>
                      <span className="text-2xl font-bold text-green-700 dark:text-green-400">
                        {targetCurrency} {convertedAmount.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-green-200 dark:border-green-800">
                      <span>Exchange rate:</span>
                      <span className="font-mono">
                        1 {expense.currency} = {exchangeRate.toFixed(4)} {targetCurrency}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Rate from: {new Date(expense.expense_date).toLocaleDateString()}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-red-600">Failed to load conversion rate</div>
                )}
              </div>
            )}

            {/* Info note */}
            <div className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950 p-3 rounded-lg">
              <p className="font-semibold mb-1">Note:</p>
              <p>This updates only the converted amount for reports and totals. The original expense amount ({expense.currency} {expense.amount.toFixed(2)}) remains unchanged.</p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsConvertDialogOpen(false)}
              disabled={isConverting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConvert}
              disabled={isConverting || isLoadingRate || convertedAmount === null || targetCurrency === expense.converted_currency}
            >
              {isConverting ? 'Updating...' : 'Update Conversion'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
